var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import TabComponent from "@widget/list/Tabs/TabComponent";
import FormField from '../inputs/FormField';
import Label from '../inputs/Label';
import Input from '../inputs/Input';
import PasswordInput from '../inputs/PasswordInput';
import Checkbox from '../inputs/Checkbox';
import RadioGroup from '../inputs/RadioGroup';
import Select from '../inputs/Select';
import classNames from 'classnames';
import TextArea from '../inputs/TextArea';
import Conditional from "../../../helper/Conditional/ConditionalComponent";
import DatePicker from "../inputs/DateTimePicker/DatePicker";
import DateTimePicker from "../inputs/DateTimePicker/DateTimePicker";
import TimePicker from "../inputs/DateTimePicker/TimePicker";
import Button from "@widget/buttons/Button";
import DateRangePicker from "../inputs/DateTimePicker/DateRangePicker";
import TimeRangePicker from "../inputs/DateTimePicker/TimeRangePicker";
import TimeRangeSliderPicker from "../inputs/DateTimePicker/TimeRangeSliderPicker";
import { UXPContext } from '@core/context/Context';
import { hasValue, toBool, toDate, toStr } from '@utils';
import { endOfDay, format, isValid, startOfDay, } from 'date-fns';
import { SkeletonLoader } from '@widget/loaders';
import { WizardStepIndicator } from './WizardStepIndicator';
import InfoButton from '@widget/popups/InfoButton';
import { Collapse } from '@widget/other/Collapse';
import useAlert from '@notifications/Alerts/useAlerts';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
const ALL_FIELDS = '*';
/**
 * Wraps formData so every key a user callback reads during render is recorded in `seen`.
 * Enumerating the object (Object.keys, spread, JSON.stringify) records ALL_FIELDS.
 * Runtime tracking is immune to minification, which renames the `data` parameter and made
 * the previous fn.toString() scanning return nothing in every production bundle.
 */
function trackReads(data, seen) {
    return new Proxy(data, {
        get(t, k) { if (typeof k === 'string')
            seen.add(k); return t[k]; },
        has(t, k) { if (typeof k === 'string')
            seen.add(k); return k in t; },
        ownKeys(t) { seen.add(ALL_FIELDS); return Reflect.ownKeys(t); },
    });
}
const MemoizedFormField = React.memo((props) => {
    var _a, _b;
    const { field, value, error, warning, formData, onChange, deps } = props;
    const { name, type, label, validate, renderField } = field;
    const placeholder = field.placeholder || "";
    // Every formData key a user callback touches below lands in `seen`; the memo
    // comparator reads it back to decide what this field actually depends on.
    // Reuse the field's existing Set so keys accumulate for the life of the field: a
    // re-render that is not caused by a formData change (an error appearing or clearing)
    // must not forget what getOptions read in its effect. A superset of the real deps
    // only costs an extra re-render; nothing is ever forgotten.
    const seen = (_a = deps.get(field.name)) !== null && _a !== void 0 ? _a : new Set();
    const tracked = trackReads(formData, seen);
    // State for async options resolution
    const [resolvedOptions, setResolvedOptions] = React.useState([]);
    const [isLoadingOptions, setIsLoadingOptions] = React.useState(false);
    // Handle async getOptions for select fields
    React.useEffect(() => {
        if (field.type !== 'select' || !field.getOptions)
            return;
        let cancelled = false;
        setIsLoadingOptions(true);
        try {
            const result = field.getOptions(tracked);
            // Handle Promise (async service call)
            if (result instanceof Promise) {
                result
                    .then((opts) => {
                    if (!cancelled) {
                        setResolvedOptions(Array.isArray(opts) ? opts : []);
                        setIsLoadingOptions(false);
                    }
                })
                    .catch((err) => {
                    if (!cancelled) {
                        console.error(`Failed to load options for field ${field.name}:`, err);
                        setResolvedOptions([]);
                        setIsLoadingOptions(false);
                    }
                });
            }
            // Handle synchronous array
            else if (Array.isArray(result)) {
                setResolvedOptions(result);
                setIsLoadingOptions(false);
            }
            else {
                setResolvedOptions([]);
                setIsLoadingOptions(false);
            }
        }
        catch (err) {
            console.error(`Error calling getOptions for field ${field.name}:`, err);
            setResolvedOptions([]);
            setIsLoadingOptions(false);
        }
        return () => {
            cancelled = true;
        };
    }, [field.type, field.getOptions, field.name, formData]);
    // Set static options if provided
    React.useEffect(() => {
        if (field.type === 'select' && field.options && !field.getOptions) {
            setResolvedOptions(Array.isArray(field.options) ? field.options : []);
        }
    }, [field.type, field.options, field.getOptions]);
    let input = null;
    switch (field.type) {
        case "checkbox":
        case "toggle":
            input = _jsx(Checkbox, { checked: toBool(value), onChange: val => onChange(val, name), label: (field === null || field === void 0 ? void 0 : field.checkboxLabel) || label, type: type == "checkbox" ? "bordered" : ((field === null || field === void 0 ? void 0 : field.toggleType) || "switch-line") });
            break;
        case "radio":
            // options: [{ label, value, description? }], as RadioGroup takes them
            input = _jsx(RadioGroup, { options: Array.isArray(field.options) ? field.options : [], selected: value, onChange: val => onChange(val, name) });
            break;
        case "select":
            const selectedOptionLabel = field === null || field === void 0 ? void 0 : field.selectedOptionLabel;
            input = _jsx(Select, { selected: value, onChange: val => onChange(val, name), options: (field === null || field === void 0 ? void 0 : field.getPaginatedOptions)
                    ? (max, lastToken, args) => field.getPaginatedOptions(tracked, max, lastToken, args)
                    : resolvedOptions, placeholder: placeholder, selectedLabel: typeof selectedOptionLabel === 'function'
                    ? (selected) => selectedOptionLabel(tracked, selected)
                    : selectedOptionLabel, labelField: hasValue(field === null || field === void 0 ? void 0 : field.labelField) ? field.labelField : 'label', valueField: hasValue(field === null || field === void 0 ? void 0 : field.valueField) ? field.valueField : 'value', 
                /* pinboard parked — see internals/pinboard-picker.md */
                searchModal: field === null || field === void 0 ? void 0 : field.searchModal });
            break;
        case "date":
            const date = hasValue(value) ? new Date(value) : null;
            const formattedDate = (!!date && isValid(date)) ? format(date, 'yyyy/MM/dd') : '';
            input = _jsx(DatePicker, { title: "", date: formattedDate, onChange: val => {
                    // the clear button sends null; format would throw on it
                    onChange(val ? format(val, "yyyy/MM/dd") : null, name);
                } });
            break;
        case "datetime":
            input = _jsx(DateTimePicker, { title: "", datetime: value, onChange: val => {
                    onChange(val, name);
                } });
            break;
        case "time":
            input = _jsx(TimePicker, { title: "", time: value, onChange: val => onChange(val ? format(val, "HH:mm:ss") : null, name) });
            break;
        case "daterange":
            input = _jsx(DateRangePicker, { title: "", startDate: (value === null || value === void 0 ? void 0 : value.startDate) || '', endDate: (value === null || value === void 0 ? void 0 : value.endDate) || '', onChange: (s, e) => {
                    const sd = isValid(s) ? startOfDay(typeof s === 'string' ? new Date(s) : s).toISOString() : null;
                    const ed = isValid(e) ? endOfDay(typeof e === 'string' ? new Date(e) : e).toISOString() : null;
                    onChange({
                        startDate: sd,
                        endDate: ed
                    }, name);
                } });
            break;
        case "timerange":
            input = _jsx(TimeRangePicker, { title: "", startTime: (value === null || value === void 0 ? void 0 : value.startTime) || '', endTime: (value === null || value === void 0 ? void 0 : value.endTime) || '', onChange: (start, end) => {
                    onChange({
                        startTime: start,
                        endTime: end
                    }, name);
                } });
            break;
        case "timerangeslider":
            input = _jsx(TimeRangeSliderPicker, { title: "", startTime: (value === null || value === void 0 ? void 0 : value.startTime) || '', endTime: (value === null || value === void 0 ? void 0 : value.endTime) || '', onChange: (start, end) => {
                    onChange({
                        startTime: start,
                        endTime: end
                    }, name);
                } });
            break;
        case 'textarea':
        case "json":
            input = _jsx(TextArea, { value: value, onChange: val => onChange(val, name) });
            break;
        case "number":
            input = _jsx(Input, { type: 'number', value: value, onChange: val => {
                    let float = parseFloat(val);
                    onChange(isNaN(float) ? 0 : float, name);
                }, placeholder: placeholder });
            break;
        case 'readonly':
            input = _jsx(Label, { children: toStr(value) });
            break;
        case 'password':
            input = _jsx(PasswordInput, { value: value, onChange: val => onChange(val, name), placeholder: placeholder });
            break;
        default:
            const _t = (hasValue(type) && ['text', 'email'].includes(type)) ? type : 'text';
            input = _jsx(Input, { type: _t, value: value, onChange: val => onChange(val, name), placeholder: placeholder });
            break;
    }
    if (type == 'hidden')
        return null;
    if (type == 'spacer')
        return _jsx("div", { className: "uxpcore_dynamicform__spacer" });
    const infoPosition = (_b = field.infoPosition) !== null && _b !== void 0 ? _b : 'label';
    const fieldContent = typeof renderField == 'function' ? renderField(tracked, (val) => onChange(val, name)) : input;
    // ref mutation, idempotent under StrictMode double render.
    // The Map holds a reference to `seen` — the same Set on every render of this field, so
    // recorded keys accumulate for its lifetime. The options effect closes over it too, so
    // keys read by getOptions land in it after this call and are still seen by the comparator.
    deps.set(field.name, seen);
    return _jsxs(FormField, { children: [_jsxs("div", { className: "uxpcore_dynamicform__field-header", children: [_jsxs(Label, { icon: field.icon, children: [label, (typeof (validate === null || validate === void 0 ? void 0 : validate.required) === 'function' ? validate.required(tracked) : validate === null || validate === void 0 ? void 0 : validate.required) ? _jsx("span", { className: "uxpcore_dynamicform__required-mark", children: " *" }) : null] }), field.info && infoPosition === 'label' && _jsx(InfoButton, { content: field.info, size: "small" })] }), field.description && (_jsx("div", { className: "uxpcore_dynamicform__field-description", children: field.description })), field.info && infoPosition === 'input'
                ? _jsxs("div", { className: "uxpcore_dynamicform__field-input-row", children: [fieldContent, _jsx(InfoButton, { content: field.info })] })
                : fieldContent, _jsx(FormError, { formErrors: { [name]: error }, field: name }), !(error === null || error === void 0 ? void 0 : error.trim()) && (warning === null || warning === void 0 ? void 0 : warning.trim()) && _jsx("div", { className: "uxpcore_dynamicform__field-warning", children: warning })] }, name);
}, (prevProps, nextProps) => {
    var _a;
    const field = nextProps.field;
    if (prevProps.value !== nextProps.value)
        return false;
    if (prevProps.error !== nextProps.error)
        return false;
    if (prevProps.warning !== nextProps.warning)
        return false;
    // static options: parent gives a new reference when they change
    if (field.type === 'select' && !field.getOptions && !field.getPaginatedOptions) {
        if (prevProps.field.options !== nextProps.field.options)
            return false;
    }
    // a rebuilt structure (new closures) must not be served by the stale ones
    const fns = ['getOptions', 'getPaginatedOptions', 'renderField', 'selectedOptionLabel', 'show'];
    if (fns.some(k => prevProps.field[k] !== nextProps.field[k]))
        return false;
    // explicit dependsOn wins; else keys recorded by trackReads on the last render;
    // unknown (first render) or ALL_FIELDS => re-render whenever formData changed
    const tracked = nextProps.deps.get(field.name);
    const deps = (_a = field.dependsOn) !== null && _a !== void 0 ? _a : (tracked ? Array.from(tracked) : [ALL_FIELDS]);
    if (deps.includes(ALL_FIELDS))
        return prevProps.formData === nextProps.formData;
    for (const dep of deps) {
        if (prevProps.formData[dep] !== nextProps.formData[dep])
            return false;
    }
    return true;
});
MemoizedFormField.displayName = 'MemoizedFormField';
/**
 * @export
 * This component provides a dynamic form component
 * Developer can pass a json structure and it will create a form component
 *
 * @category Forms/Dynamic Form
 */
const DynamicForm = React.forwardRef((props, ref) => {
    var _a, _b, _c;
    const [formData, setFormData] = React.useState({});
    const [isDataLoaded, setIsDataLoaded] = React.useState(false);
    const [formErrors, setFormErrors] = React.useState({});
    // non-blocking validator hints ({ valid: true, warning }), by field name
    const [formWarnings, setFormWarnings] = React.useState({});
    const [processing, setProcessing] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [tabs, setTabs] = React.useState([]);
    const [selectedTab, setSelectedTab] = React.useState(null);
    const formDataRef = React.useRef({});
    // formErrors, readable from a stale closure. handleChange is rebuilt every render but the
    // MemoizedFormField comparator ignores onChange, so a memoised field can call an old one.
    const formErrorsRef = React.useRef({});
    // Keys written by setErrors() rather than by a validator - backend messages such as
    // "name already taken" that no client-side rule can reproduce. Left alone when pruning.
    const pinnedErrorsRef = React.useRef(new Set());
    // Raised only by an explicit, programmatic error-set - Save, wizard Next, or a consumer
    // calling setErrors. Typing must never move the user out of the tab they are working in.
    const jumpToErrorRef = React.useRef(false);
    // bumped by every change; a prune that finishes after a newer change must not write
    const changeSeqRef = React.useRef(0);
    // field name -> formData keys that field's callbacks read on their last render (see trackReads)
    const fieldDeps = React.useRef(new Map());
    // Serialized snapshot of the seeded values, used to compute isDirty().
    const initialSerializedRef = React.useRef('');
    const context = React.useContext(UXPContext);
    // Used only inside handleSubmitExternal (a plain function, not a useCallback) — never put
    // this in a deps array: useAlert returns a fresh object each render and would loop.
    const alerts = useAlert();
    // Stable-ish serialization for dirty comparison. Normalizes Date -> ISO so a live
    // Date field compares equal to its seeded ISO string.
    // ponytail: JSON compare; relies on stable key order (both sides built from the same
    // field set). Good enough for form scalars/dates/ranges; swap for a deep-equal if a
    // field ever holds an unordered map.
    const serializeForDirty = (data) => {
        try {
            return JSON.stringify(data, (_k, v) => (v instanceof Date ? (isValid(v) ? v.toISOString() : null) : v));
        }
        catch (_a) {
            return '';
        }
    };
    const isMobile = useIsMobileLayout();
    // Phones, tabs mode: the form buttons sit in the tab slider row instead of the footer
    const buttonsInTabs = isMobile && !props.hideButtons;
    const renderStyle = (!!(props === null || props === void 0 ? void 0 : props.renderOptions) && !!((_a = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _a === void 0 ? void 0 : _a.renderStyle)) ? (_b = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _b === void 0 ? void 0 : _b.renderStyle : 'standard';
    const inSlider = buttonsInTabs && renderStyle === 'tabs';
    // Expose methods to parent component
    React.useImperativeHandle(ref, () => ({
        submit: handleSubmitExternal,
        cancel: handleCancel,
        validate: validateForm,
        getFormData: () => formDataRef.current,
        setFormData: updateFormData,
        getErrors: () => formErrors,
        clearErrors: () => replaceFormErrors({}),
        setErrors: setErrorsExternal,
        clearFieldError: clearFieldErrorExternal,
        isProcessing: () => processing,
        goToNext: onClickNext,
        goToPrevious: onClickPrevious,
        goToStep,
        isDirty: () => serializeForDirty(formDataRef.current) !== initialSerializedRef.current,
        getWizardState: () => {
            if (renderStyle !== 'wizard' || tabs.length === 0)
                return null;
            const currentIndex = tabs.findIndex(t => t.id === selectedTab);
            const currentStep = currentIndex >= 0 ? currentIndex : 0;
            return {
                currentStep: currentStep + 1, // 1-indexed for display
                totalSteps: tabs.length,
                isFirstStep: currentStep === 0,
                isLastStep: currentStep === tabs.length - 1
            };
        }
    }));
    // Reseed only when fields or their seeded values actually change — parents recreate
    // formStructure inline on every render; resetting on identity wipes user edits.
    const structureSignature = React.useMemo(() => {
        try {
            return (props.formStructure || []).map(section => {
                const allFields = section.sections ? section.sections.flatMap(s => s.fields || []) : section.fields;
                return (allFields || []).map(f => { var _a; return `${f.name}:${f.type}:${JSON.stringify((_a = f.value) !== null && _a !== void 0 ? _a : null)}`; }).join(',');
            }).join('|');
        }
        catch (_a) {
            return String(Date.now()); // unserializable value — fall back to reseed-every-render (old behavior)
        }
    }, [props.formStructure]);
    React.useEffect(() => {
        updateFormDataWithDefaults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [structureSignature, props.isLoading]);
    React.useEffect(() => {
        if (renderStyle !== 'standard') {
            const _tabs = (props.formStructure || []).map((section, i) => {
                // Collect all fields for validation/error tracking — from flat fields or sub-sections
                const allFields = section.sections
                    ? section.sections.flatMap(s => s.fields)
                    : section.fields;
                return {
                    id: `tab-${i + 1}`,
                    label: (section === null || section === void 0 ? void 0 : section.title) || `Section ${i + 1}`,
                    content: _jsx(_Fragment, {}),
                    fields: allFields.filter(f => f.type !== 'spacer').map(f => f.name),
                    index: i,
                    visibleFields: allFields.filter(f => f.type !== 'hidden'),
                    columns: section.columns || 1,
                    maxColumnWidth: section.maxColumnWidth,
                    show: section.show,
                    section
                };
            });
            setTabs(_tabs);
        }
        else {
            setTabs([]);
            setSelectedTab(null);
        }
    }, [props.formStructure, renderStyle, formData]);
    React.useEffect(() => {
        var _a;
        const jump = jumpToErrorRef.current;
        jumpToErrorRef.current = false;
        // Errors changing because someone typed - or because a cross-field error was cleared -
        // must not reselect the tab. Only an explicit validation takes the user to the error.
        if (jump) {
            setSelectedTab(getActiveTab(tabs));
            return;
        }
        // same fallback getActiveTab applies when there is nothing to jump to, plus a validity
        // check: the old unconditional call self-healed a selection that no longer exists
        if (!selectedTab || !tabs.some(t => t.id === selectedTab))
            setSelectedTab(((_a = tabs === null || tabs === void 0 ? void 0 : tabs[0]) === null || _a === void 0 ? void 0 : _a.id) || null);
    }, [tabs, formErrors]);
    // Notify parent of wizard state changes
    React.useEffect(() => {
        if (props.onWizardStateChange) {
            if (renderStyle === 'wizard' && tabs.length > 0 && selectedTab) {
                const currentIndex = tabs.findIndex(t => t.id === selectedTab);
                const currentStep = currentIndex >= 0 ? currentIndex : 0;
                const wizardState = {
                    currentStep: currentStep + 1,
                    totalSteps: tabs.length,
                    isFirstStep: currentStep === 0,
                    isLastStep: currentStep === tabs.length - 1
                };
                props.onWizardStateChange(wizardState);
            }
            else {
                props.onWizardStateChange(null);
            }
        }
    }, [renderStyle, tabs, selectedTab, props.onWizardStateChange]);
    React.useEffect(() => { setLoading(props.isLoading || false); }, [props.isLoading]);
    React.useEffect(() => {
        if (!isDataLoaded && Object.keys(formData).length > 0) {
            setIsDataLoaded(true);
        }
    }, [formData, isDataLoaded]);
    function updateFormData(data) {
        setFormData(data);
        formDataRef.current = data;
    }
    // Same contract as updateFormData: state and ref move together, so anything reading the ref
    // sees the latest errors even from a stale closure.
    function updateFormErrors(update) {
        const next = typeof update === 'function' ? update(formErrorsRef.current) : update;
        formErrorsRef.current = next;
        setFormErrors(next);
    }
    // The whole map was replaced, so nothing in it came from setErrors any more.
    function replaceFormErrors(errors) {
        pinnedErrorsRef.current.clear();
        updateFormErrors(errors);
    }
    function setFieldWarning(key, warning) {
        setFormWarnings(prev => {
            if (warning)
                return prev[key] === warning ? prev : Object.assign(Object.assign({}, prev), { [key]: warning });
            if (!(key in prev))
                return prev;
            const next = Object.assign({}, prev);
            delete next[key];
            return next;
        });
    }
    function updateFormDataWithDefaults() {
        // the form is being reseeded, so pins recorded against the previous record no longer apply
        pinnedErrorsRef.current.clear();
        setFormWarnings({});
        let newData = {};
        for (const section of (props === null || props === void 0 ? void 0 : props.formStructure) || []) {
            const allFields = section.sections
                ? section.sections.flatMap(s => s.fields)
                : section.fields;
            for (const field of allFields) {
                let type = field.type || "text";
                if (type === 'spacer')
                    continue;
                let value = field.value;
                switch (type) {
                    case "checkbox":
                    case "toggle":
                        if (!value) {
                            value = false;
                        }
                        else if (typeof value != 'boolean') {
                            value = !!value;
                        } // string 'false' will return true
                        break;
                    case "number":
                        if (!value) {
                            value = 0;
                        }
                        else if (typeof value == 'number' || typeof value == 'string') {
                            // assume float value is provided 
                            let float = typeof value == 'number' ? value : parseFloat(value);
                            value = (isNaN(float) || !isFinite(float)) ? 0 : float;
                        }
                        break;
                    case "date":
                    case "datetime": {
                        // value can be a Date instance (DateTimePicker onChange emits Date objects)
                        if (value instanceof Date) {
                            value = isValid(value) ? value.toISOString() : null;
                        }
                        else if (hasValue(value) && typeof value == 'string') {
                            const date = new Date(value);
                            if (isValid(date))
                                value = date.toISOString();
                            else
                                value = null;
                        }
                        else
                            value = null;
                        break;
                    }
                    case "daterange": {
                        if (!!value) {
                            value = {
                                startDate: (!!(value === null || value === void 0 ? void 0 : value.startDate)) ? toDate(value === null || value === void 0 ? void 0 : value.startDate) : '',
                                endDate: (!!(value === null || value === void 0 ? void 0 : value.endDate)) ? toDate(value === null || value === void 0 ? void 0 : value.endDate) : ''
                            };
                        }
                        else
                            value = { startDate: '', endDate: '' };
                        break;
                    }
                    case "timerange": {
                        if (!!value) {
                            value = {
                                startTime: (!!(value === null || value === void 0 ? void 0 : value.startTime)) ? toDate(value === null || value === void 0 ? void 0 : value.startTime) : '',
                                endTime: (!!(value === null || value === void 0 ? void 0 : value.endTime)) ? toDate(value === null || value === void 0 ? void 0 : value.endTime) : ''
                            };
                        }
                        else
                            value = { startTime: '', endTime: '' };
                        break;
                    }
                    case "timerangeslider": {
                        if (!!value) {
                            value = {
                                startTime: (!!(value === null || value === void 0 ? void 0 : value.startTime)) ? toDate(value === null || value === void 0 ? void 0 : value.startTime) : '',
                                endTime: (!!(value === null || value === void 0 ? void 0 : value.endTime)) ? toDate(value === null || value === void 0 ? void 0 : value.endTime) : ''
                            };
                        }
                        else
                            value = { startTime: '', endTime: '' };
                        break;
                    }
                    case 'json':
                        value = value;
                    default:
                        if (!value)
                            value = "";
                        break;
                }
                Object.assign(newData, { [field.name]: value });
            }
        }
        updateFormData(newData);
        // Baseline for isDirty(): the freshly seeded values (also reset on reseed).
        initialSerializedRef.current = serializeForDirty(newData);
    }
    // Validate entire form
    function validateForm(_data) {
        return __awaiter(this, void 0, void 0, function* () {
            let errors = {};
            let warnings = {};
            let isValidForm = true;
            const _formData = !!_data ? _data : formDataRef.current;
            for (const section of props.formStructure) {
                const allFields = section.sections
                    ? section.sections.flatMap(s => s.fields)
                    : section.fields;
                for (const field of allFields) {
                    if (field.type === 'spacer')
                        continue;
                    let key = field.name;
                    let val = _formData[key];
                    let { valid, error, warning } = yield validateField(key, val, _formData);
                    if (!valid) {
                        errors[key] = error;
                        isValidForm = false;
                    }
                    else if (warning)
                        warnings[key] = warning;
                }
            }
            setFormWarnings(warnings);
            // an explicit validation (Save, or validate() on the ref) may move the user to the error.
            // only ever set it - clearing here would cancel a jump another caller just asked for.
            if (!isValidForm)
                jumpToErrorRef.current = true;
            replaceFormErrors(errors);
            return isValidForm;
        });
    }
    // External submit handler
    function handleSubmitExternal() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                if (processing)
                    return;
                setProcessing(true);
                const _formData = (props === null || props === void 0 ? void 0 : props.beforeSubmit) ? yield props.beforeSubmit(formDataRef.current) : formDataRef.current;
                const isValid = yield validateForm(_formData);
                if (!isValid) {
                    setProcessing(false);
                    return;
                }
                yield props.onSubmit(_formData);
                setProcessing(false);
            }
            catch (e) {
                console.error('Unable to submit. Error: ', e);
                setProcessing(false);
                /*
                 * Present it here, once, for every entry point — Save button, native <form>, imperative
                 * submit(). A throw from onSubmit used to be re-thrown into a ButtonComponent that only
                 * logs, or into the native form handler with no catcher at all, so the user saw nothing.
                 * Convention (Location app, and most handlers in the platform) is to handle inline with
                 * alerts.error and return normally — this is only a net for handlers that throw instead.
                 * Legacy executeService throws the bare error STRING, hence the typeof branch.
                 */
                const message = (e === null || e === void 0 ? void 0 : e.message) ||
                    (typeof e === 'string' ? e : '') ||
                    context.$L('uxp-core.text.something-went-wrong');
                // optional-chained: DynamicForm is on window.UXPComponents and an external widget may
                // render it outside NotificationProvider, where useAlert's context has no methods.
                (_a = alerts === null || alerts === void 0 ? void 0 : alerts.error) === null || _a === void 0 ? void 0 : _a.call(alerts, message);
                // deliberately NOT re-thrown — re-throwing is what produced the silent swallow
            }
        });
    }
    // submit event
    function handleSubmit(e) {
        return __awaiter(this, void 0, void 0, function* () {
            e.preventDefault();
            e.stopPropagation();
            yield handleSubmitExternal();
        });
    }
    const handleCancel = () => {
        var _a;
        formDataRef.current = {};
        updateFormDataWithDefaults();
        (_a = props === null || props === void 0 ? void 0 : props.onCancel) === null || _a === void 0 ? void 0 : _a.call(props);
    };
    const setErrorsExternal = (errors) => {
        Object.keys(errors).forEach(name => pinnedErrorsRef.current.add(name));
        if (Object.keys(errors).length > 0)
            jumpToErrorRef.current = true;
        updateFormErrors((prev) => (Object.assign(Object.assign({}, prev), errors)));
    };
    const clearFieldErrorExternal = (fieldName) => {
        const names = Array.isArray(fieldName) ? fieldName : [fieldName];
        names.forEach(name => pinnedErrorsRef.current.delete(name));
        updateFormErrors((prev) => {
            const newErrors = Object.assign({}, prev);
            names.forEach(name => delete newErrors[name]);
            return newErrors;
        });
    };
    // change event
    const handleChange = (value, key) => __awaiter(void 0, void 0, void 0, function* () {
        const allFields = props.formStructure.flatMap(s => s.sections ? s.sections.flatMap(sub => sub.fields) : s.fields);
        // definition 
        const fieldDef = allFields.find(f => f.name === key);
        // format value before change 
        if (fieldDef && (fieldDef === null || fieldDef === void 0 ? void 0 : fieldDef.formatter)) {
            value = fieldDef.formatter(value);
        }
        updateFormData(Object.assign(Object.assign({}, formDataRef.current), { [key]: value }));
        // setFormData(prev => ({ ...prev, [key]: value }));
        // formDataRef.current = { ...formDataRef.current, [key]: value }
        const seq = ++changeSeqRef.current;
        // the field was just edited, so whatever it shows now comes from validation, not setErrors.
        // done before the await: a setErrors landing mid-validation must keep its pin.
        pinnedErrorsRef.current.delete(key);
        let { valid, error, warning } = yield validateField(key, value, formDataRef.current);
        // a slower validation of an older keystroke must not overwrite a newer hint
        if (seq === changeSeqRef.current)
            setFieldWarning(key, valid ? warning : undefined);
        // remove erros if valid
        if (valid) {
            updateFormErrors((prev) => {
                const newErrors = Object.assign({}, prev);
                delete newErrors[key];
                return newErrors;
            });
        }
        // add if not
        else
            updateFormErrors((prev) => (Object.assign(Object.assign({}, prev), { [key]: error })));
        yield clearResolvedErrors(key, seq);
    });
    /**
     * A field's `required` or `customValidateFunction` may read other fields off formData, so
     * changing one field can make another field's error untrue - untick "Receive messages by
     * email" and the "Email is required" it raised no longer applies. Only the changed field is
     * validated above, so re-check the ones already showing an error and drop those that now pass.
     *
     * Deliberately clear-only. A field the user has not touched must never start complaining
     * because a different field changed, and nothing is lost by being lenient here: Save runs the
     * full validateForm before onSubmit, so a genuine error comes straight back.
     *
     * Errors pinned by setErrors() are skipped - they came from the backend and no client-side
     * rule can reproduce them, so re-validating would always "pass" and wipe the message.
     */
    function clearResolvedErrors(changedKey, seq) {
        return __awaiter(this, void 0, void 0, function* () {
            const candidates = Object.keys(formErrorsRef.current)
                .filter(name => name !== changedKey && !pinnedErrorsRef.current.has(name));
            if (candidates.length === 0)
                return;
            const resolved = [];
            for (const name of candidates) {
                try {
                    const { valid } = yield validateField(name, formDataRef.current[name], formDataRef.current);
                    // only the pass/fail matters; never write the message back - validateField returns a
                    // hardcoded 'Unknown field' for anything not in formStructure
                    if (valid)
                        resolved.push(name);
                }
                catch (_a) {
                    // a consumer's validator threw; leave that field's error alone rather than let the
                    // failure escape into the keystroke handler of the field being edited
                }
            }
            // a newer change started while we were awaiting - it owns the error map now, and deleting
            // here would wipe an error its validation has just recorded
            if (seq !== changeSeqRef.current)
                return;
            if (resolved.length === 0)
                return;
            updateFormErrors((prev) => {
                const newErrors = Object.assign({}, prev);
                resolved.forEach(name => delete newErrors[name]);
                return newErrors;
            });
        });
    }
    function validateField(key, value, _formData) {
        return __awaiter(this, void 0, void 0, function* () {
            const allFields = props.formStructure.flatMap(s => s.sections ? s.sections.flatMap(sub => sub.fields) : s.fields);
            let field = allFields.find(f => f.name == key);
            if (!field)
                return { valid: false, error: 'Unknown field' };
            let validator = field.validate;
            if (!validator)
                return { valid: true };
            let { required, requiredMessage, allowEmptyString, minLength, maxLength, regExp, allowZero, allowNegative, minVal, maxVal, customValidateFunction } = validator;
            const isRequired = typeof required === 'function' ? required(_formData) : !!required;
            const isEmpty = !value || (typeof value == 'string' && !allowEmptyString && value.trim().length == 0);
            if (isRequired && isEmpty) {
                const message = typeof requiredMessage === 'function' ? requiredMessage(_formData) : requiredMessage;
                return { valid: false, error: (typeof message === 'string' && message.trim()) ? message : context.$L('uxp-core.form-error.required') };
            }
            // constraint checks run for ANY non-empty value, required or not
            if (!isEmpty) {
                let type = typeof value;
                if (type == 'string') {
                    if (minLength && value.trim().length < minLength)
                        return { valid: false, error: context.$L('uxp-core.form-error.min-length', { minLength }) };
                    if (maxLength && value.trim().length > maxLength)
                        return { valid: false, error: context.$L('uxp-core.form-error.max-length', { maxLength }) };
                }
                if (type == 'number') {
                    if (!allowNegative && Number(value) < 0)
                        return { valid: false, error: context.$L('uxp-core.form-error.value-less-than-zero') };
                    if (!allowZero && value == 0)
                        return { valid: false, error: context.$L('uxp-core.form-error.value-less-than-zero') };
                    if (minVal && Number(value) < minVal)
                        return { valid: false, error: context.$L('uxp-core.form-error.value-less-than-min', { minVal }) };
                    if (maxVal && Number(value) > maxVal)
                        return { valid: false, error: context.$L('uxp-core.form-error.value-greater-than-max', { maxVal }) };
                }
            }
            if (value && regExp && !regExp.test(value))
                return { valid: false, error: context.$L('uxp-core.form-error.invalid') };
            if (customValidateFunction)
                return yield Promise.resolve(customValidateFunction(value, _formData));
            return { valid: true };
        });
    }
    function getActiveTab(tabs) {
        var _a, _b;
        const firstError = (_a = Object.keys(formErrors)) === null || _a === void 0 ? void 0 : _a[0];
        if (Object.keys(formErrors).length > 0 && !!firstError) {
            const tab = tabs.find(t => t.fields.includes(firstError));
            if (tab)
                return `tab-${tab.index + 1}`;
        }
        if (!!selectedTab)
            return selectedTab;
        return ((_b = tabs === null || tabs === void 0 ? void 0 : tabs[0]) === null || _b === void 0 ? void 0 : _b.id) || null;
    }
    function renderField(formField) {
        return (_jsx(MemoizedFormField, { field: formField, value: formData[formField.name], error: formErrors[formField.name], warning: formWarnings[formField.name], formData: formData, onChange: handleChange, deps: fieldDeps.current }, formField.name));
    }
    function renderFormSectionContent(cols, visibleFields, maxColumnWidth) {
        const _maxWidth = maxColumnWidth || '30rem';
        return _jsx(FormRow, { cols: cols, maxColumnWidth: _maxWidth, children: visibleFields.map((field, k) => {
                return _jsx(Conditional, { visible: field.show ? field.show(formData) : true, children: _jsx("div", { className: 'uxpcore_dynamicform__form-field', children: renderField(field) }) }, k);
            }) });
    }
    function renderSectionBody(section) {
        if (section.sections) {
            return section.sections.map((sub, i) => (_jsx(FormSection, { title: sub.title, show: sub.show ? sub.show(formData) : true, seperator: sub.seperator, collapsible: sub.collapsible, defaultExpanded: sub.defaultExpanded, children: renderFormSectionContent(sub.columns || 1, sub.fields.filter(f => f.type !== 'hidden'), sub.maxColumnWidth) }, i)));
        }
        return renderFormSectionContent(section.columns || 1, section.fields.filter(f => f.type !== 'hidden'), section.maxColumnWidth);
    }
    function renderStandardForm() {
        return (props.formStructure || []).map((section, i) => {
            return _jsx(FormSection, { title: section.title, show: (section === null || section === void 0 ? void 0 : section.show) ? section.show(formData) : true, seperator: section.seperator, collapsible: section.collapsible, defaultExpanded: section.defaultExpanded, children: renderSectionBody(section) }, i);
        });
    }
    function extractTabStyles() {
        var _a, _b;
        const { tabBackgroundColor, tabTextColor, activeTabBackgroundColor, activeTabTextColor, errorTabBackgroundColor, errorTabTextColor, disabledTabBackgroundColor, disabledTabTextColor } = props.renderOptions;
        return {
            tabBackgroundColor: hasValue(tabBackgroundColor) ? tabBackgroundColor : null,
            tabTextColor: hasValue(tabTextColor) ? tabTextColor : null,
            selectedTabBackgroundColor: hasValue(activeTabBackgroundColor) ? activeTabBackgroundColor : null,
            selectedTabTextColor: hasValue(activeTabTextColor) ? activeTabTextColor : null,
            tabHighlightBackgroundColor: hasValue(errorTabBackgroundColor) ? errorTabBackgroundColor : (_a = context === null || context === void 0 ? void 0 : context.theme) === null || _a === void 0 ? void 0 : _a.primaryBGColor,
            tabHighlightTextColor: hasValue(errorTabTextColor) ? errorTabTextColor : (_b = context === null || context === void 0 ? void 0 : context.theme) === null || _b === void 0 ? void 0 : _b.toastErrorBGColor,
            disabledTabBackgroundColor: hasValue(disabledTabBackgroundColor) ? disabledTabBackgroundColor : null,
            disabledTabTextColor: hasValue(disabledTabTextColor) ? disabledTabTextColor : null
        };
    }
    function renderTabbedForm() {
        var _a, _b, _c;
        const _tabs = (tabs || [])
            .filter((extended) => {
            // Only include tabs where show is undefined or returns true
            return extended.show ? extended.show(formData) : true;
        })
            .map((extended) => {
            return {
                id: extended.id,
                label: extended.label,
                content: renderSectionBody(extended.section),
                highlight: Object.keys(formErrors).some((a) => extended.fields.includes(a)),
                disabled: renderStyle == 'wizard' && extended.id !== selectedTab
            };
        });
        // Wizard mode: use step indicator instead of tabs
        if (renderStyle === 'wizard') {
            const wizardSteps = _tabs.map(tab => ({
                id: tab.id,
                label: typeof tab.label === 'string' ? tab.label : String(tab.label),
                hasError: tab.highlight || false
            }));
            // Get completed steps (all steps before current step)
            const currentIndex = _tabs.findIndex(t => t.id === selectedTab);
            const completedSteps = currentIndex > 0
                ? _tabs.slice(0, currentIndex).map(t => t.id)
                : [];
            return (_jsxs(_Fragment, { children: [!((_a = props.renderOptions) === null || _a === void 0 ? void 0 : _a.hideStepIndicator) && (_jsx(WizardStepIndicator, { steps: wizardSteps, currentStepId: selectedTab, completedSteps: completedSteps })), _jsx("div", { className: "uxpcore_dynamicform__wizard-content", children: (_b = _tabs.find(t => t.id === selectedTab)) === null || _b === void 0 ? void 0 : _b.content })] }));
        }
        // Tabs mode: use TabComponent
        return _jsx(TabComponent, { tabs: _tabs, onChangeTab: setSelectedTab, selected: selectedTab, direction: (_c = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _c === void 0 ? void 0 : _c.direction, position: 'top', styles: extractTabStyles(), rightContent: buttonsInTabs ? renderButtons() : undefined });
    }
    function generateForm() {
        switch (renderStyle) {
            case 'tabs':
            case 'wizard':
                return renderTabbedForm();
            default:
                return renderStandardForm();
        }
    }
    function onClickPrevious() {
        var _a, _b;
        const currentIndex = tabs.findIndex(t => t.id == selectedTab);
        if (currentIndex <= 0) {
            setSelectedTab(((_a = tabs === null || tabs === void 0 ? void 0 : tabs[0]) === null || _a === void 0 ? void 0 : _a.id) || null);
        }
        setSelectedTab((((_b = tabs === null || tabs === void 0 ? void 0 : tabs[currentIndex - 1]) === null || _b === void 0 ? void 0 : _b.id) || null));
    }
    /**
     * Validate the given fields. On a pass their old errors are dropped (or their step keeps the
     * error mark); on a fail the errors are shown and the form jumps to the first one.
     */
    function validateStepFields(fields, data) {
        return __awaiter(this, void 0, void 0, function* () {
            // Validate all fields concurrently
            const validationResults = yield Promise.all(fields.map((field) => __awaiter(this, void 0, void 0, function* () {
                const val = data[field];
                const { valid, error } = yield validateField(field, val, data);
                return { field, valid, error };
            })));
            // Reduce the results
            const { isValid, errors } = validationResults.reduce((res, { field, valid, error }) => {
                res.isValid = res.isValid && valid;
                if (!valid)
                    res.errors[field] = error;
                return res;
            }, { isValid: true, errors: {} });
            if (fields.length == 0 || isValid) {
                if (fields.some(f => f in formErrorsRef.current)) {
                    updateFormErrors((prev) => {
                        const newErrors = Object.assign({}, prev);
                        fields.forEach(f => delete newErrors[f]);
                        return newErrors;
                    });
                }
                return true;
            }
            jumpToErrorRef.current = true;
            replaceFormErrors(errors);
            return false;
        });
    }
    function onClickNext() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const currentTab = tabs.find(t => t.id == selectedTab);
            if (yield validateStepFields(currentTab.fields || [], formData)) {
                setSelectedTab((((_a = tabs === null || tabs === void 0 ? void 0 : tabs[currentTab.index + 1]) === null || _a === void 0 ? void 0 : _a.id) || null));
            }
        });
    }
    function goToStep(index) {
        return __awaiter(this, void 0, void 0, function* () {
            const target = tabs[index];
            if (!target)
                return false;
            // every step before the target must pass, so this cannot be used to skip a required step
            const fields = tabs.slice(0, index).flatMap(t => t.fields || []);
            if (!(yield validateStepFields(fields, formDataRef.current)))
                return false;
            setSelectedTab(target.id);
            return true;
        });
    }
    function renderSkeletonForm() {
        return (props.formStructure || []).map((section, i) => {
            const visibleFields = section.fields.filter(f => f.type !== 'hidden');
            const cols = section.columns || 1;
            return (_jsxs("div", { className: classNames('uxpcore_dynamicform__form-section', 'uxpcore_dynamicform__skeleton', {
                    'uxpcore_dynamicform__form-section--has-seperator': section === null || section === void 0 ? void 0 : section.seperator
                }), children: [section.title && (_jsx("div", { className: 'uxpcore_dynamicform__form-section-label', children: _jsx(SkeletonLoader, { width: "150px", height: "20px", additionalStyle: { marginBottom: '10px' } }) })), _jsx("div", { className: 'uxpcore_dynamicform__form-section-content', children: _jsx(FormRow, { cols: cols, maxColumnWidth: section.maxColumnWidth, children: visibleFields.map((_, k) => (_jsx("div", { className: 'uxpcore_dynamicform__form-field', children: _jsxs(FormField, { children: [_jsx(SkeletonLoader, { width: "120px", height: "14px", additionalStyle: { marginBottom: '8px' } }), _jsx(SkeletonLoader, { width: "100%", height: "40px", additionalStyle: { borderRadius: '4px' } })] }) }, k))) }) })] }, i));
        });
    }
    function renderSkeletonByStyle() {
        switch (renderStyle) {
            case 'tabs':
            case 'wizard':
                // For tabs/wizard, render skeleton tabs + skeleton fields
                return (_jsxs("div", { className: "uxpcore_dynamicform__skeleton", children: [_jsx("div", { style: { display: 'flex', gap: '10px', marginBottom: '20px' }, children: props.formStructure.map((_, i) => (_jsx(SkeletonLoader, { width: "100px", height: "40px", additionalStyle: { borderRadius: '4px' } }, i))) }), props.formStructure.length > 0 && (() => {
                            const firstSection = props.formStructure[0];
                            const visibleFields = firstSection.fields.filter(f => f.type !== 'hidden');
                            const cols = firstSection.columns || 1;
                            return (_jsx("div", { className: 'uxpcore_dynamicform__form-section-content', children: _jsx(FormRow, { cols: cols, maxColumnWidth: firstSection.maxColumnWidth, children: visibleFields.map((_, k) => (_jsx("div", { className: 'uxpcore_dynamicform__form-field', children: _jsxs(FormField, { children: [_jsx(SkeletonLoader, { width: "120px", height: "14px", additionalStyle: { marginBottom: '8px' } }), _jsx(SkeletonLoader, { width: "100%", height: "40px", additionalStyle: { borderRadius: '4px' } })] }) }, k))) }) }));
                        })()] }));
            default:
                // Standard form skeleton
                return renderSkeletonForm();
        }
    }
    function renderButtons() {
        var _a, _b, _c, _d, _e, _f;
        // Don't render buttons if hideButtons is true
        if (props.hideButtons)
            return null;
        const btnSize = props.buttonSize;
        if (renderStyle == 'wizard') {
            const isLastStep = tabs.findIndex(t => t.id == selectedTab) == (tabs.length - 1);
            const isFirstStep = tabs.findIndex(t => t.id == selectedTab) == 0;
            if (isLastStep) {
                return _jsxs(_Fragment, { children: [_jsx(Button, { title: ((_a = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _a === void 0 ? void 0 : _a.previousButtonLabel) ? (_b = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _b === void 0 ? void 0 : _b.previousButtonLabel : context.$L('uxp-core.text.previous'), onClick: onClickPrevious, type: "button", leftIcon: 'fas chevron-left', className: 'dynamic-form-secondary-btn', variant: 'secondary', size: btnSize }), !(props === null || props === void 0 ? void 0 : props.hideCancelButton) && !isMobile ?
                            _jsx(Button, { title: props.cancelButtonLabel ? props.cancelButtonLabel : context.$L('uxp-core.text.cancel'), onClick: handleCancel, type: "reset", leftIcon: (props === null || props === void 0 ? void 0 : props.cancelButtonLabel) || 'fas times', className: 'dynamic-form-secondary-btn', variant: 'secondary', size: btnSize })
                            : null, _jsx(Button, { title: props.submitButtonLabel ? props.submitButtonLabel : context.$L('uxp-core.text.submit'), loadingTitle: props.submitButtonLoadingLabel ? props.submitButtonLoadingLabel : context.$L('uxp-core.text.submiting'), onClick: () => { }, loading: processing, type: "submit", leftIcon: (props === null || props === void 0 ? void 0 : props.submitButtonIcon) || 'fas save', className: 'dynamic-form-primary-btn', variant: 'primary', size: btnSize })] });
            }
            else {
                return _jsxs(_Fragment, { children: [!isFirstStep ?
                            _jsx(Button, { title: ((_c = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _c === void 0 ? void 0 : _c.previousButtonLabel) ? (_d = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _d === void 0 ? void 0 : _d.previousButtonLabel : context.$L('uxp-core.text.previous'), onClick: onClickPrevious, type: "button", leftIcon: 'fas chevron-left', className: 'dynamic-form-secondary-btn', variant: 'secondary', size: btnSize })
                            : null, _jsx(Button, { title: ((_e = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _e === void 0 ? void 0 : _e.nextButtonLabel) ? (_f = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _f === void 0 ? void 0 : _f.nextButtonLabel : context.$L('uxp-core.text.next'), onClick: onClickNext, type: "button", rightIcon: 'fas chevron-right', className: 'dynamic-form-primary-btn', variant: 'primary', size: btnSize })] });
            }
        }
        else {
            // Phones, tabs mode: Submit only, next to the tab slider (Cancel doesn't fit the row)
            return _jsxs(_Fragment, { children: [!(props === null || props === void 0 ? void 0 : props.hideCancelButton) && !(buttonsInTabs && renderStyle === 'tabs') ?
                        _jsx(Button, { title: props.cancelButtonLabel ? props.cancelButtonLabel : context.$L('uxp-core.text.cancel'), onClick: handleCancel, type: "reset", leftIcon: (props === null || props === void 0 ? void 0 : props.cancelButtonIcon) || 'fas times', className: 'dynamic-form-secondary-btn', variant: 'secondary', size: btnSize })
                        : null, _jsx(Button, { title: props.submitButtonLabel ? props.submitButtonLabel : context.$L('uxp-core.text.submit'), loadingTitle: props.submitButtonLoadingLabel ? props.submitButtonLoadingLabel : context.$L('uxp-core.text.submiting'), 
                        // Phones, tabs mode: the button may sit in an outer toolbar, outside the <form>
                        onClick: inSlider ? () => { handleSubmitExternal(); } : () => { }, loading: processing, type: inSlider ? "button" : "submit", leftIcon: (props === null || props === void 0 ? void 0 : props.submitButtonIcon) || 'fas save', className: 'dynamic-form-primary-btn', variant: 'primary', size: btnSize })] });
        }
    }
    return (_jsx(_Fragment, { children: _jsx("div", { className: classNames('uxpcore_dynamicform__container', {
                'uxpcore_dynamicform--wizard': ((_c = props.renderOptions) === null || _c === void 0 ? void 0 : _c.renderStyle) === 'wizard',
            }), style: (props === null || props === void 0 ? void 0 : props.formContainerStyles) || {}, children: _jsxs("form", { onSubmit: handleSubmit, className: 'uxpcore_dynamicform__form', children: [_jsx("div", { className: "uxpcore_dynamicform__body", children: (loading || !isDataLoaded) ? renderSkeletonByStyle() : generateForm() }), !props.hideButtons && !(buttonsInTabs && renderStyle === 'tabs' && !(loading || !isDataLoaded)) && (_jsx("div", { className: "uxpcore_dynamicform__footer", children: renderButtons() }))] }) }) }));
});
const FormError = (props) => {
    const { formErrors, field } = props;
    return _jsx(_Fragment, { children: formErrors && formErrors[field] && formErrors[field].trim().length > 0 &&
            _jsx("div", { className: "uxpcore_dynamicform__field-error", children: formErrors[field] }) });
};
const FormSection = (props) => {
    const { show, title, seperator, children, collapsible = false, defaultExpanded = true } = props;
    return _jsx(Conditional, { visible: show, children: _jsx("div", { className: classNames('uxpcore_dynamicform__form-section', { 'uxpcore_dynamicform__form-section--has-seperator': seperator }), children: collapsible && hasValue(title)
                ? _jsx(Collapse, { title: title, defaultExpanded: defaultExpanded, children: _jsx("div", { className: 'uxpcore_dynamicform__form-section-content', children: children }) })
                : _jsxs(_Fragment, { children: [_jsx(Conditional, { visible: hasValue(title), children: _jsx("div", { className: 'uxpcore_dynamicform__form-section-label', children: title }) }), _jsx("div", { className: 'uxpcore_dynamicform__form-section-content', children: children })] }) }) });
};
const FormRow = (props) => {
    const maxWidth = props.maxColumnWidth || '25rem';
    return _jsx("div", { className: 'uxpcore_dynamicform__form-row', 
        // a variable, so the phone styles can set one column without !important
        style: { '--uxp-form-columns': `repeat(${props.cols}, minmax(0, ${maxWidth}))` }, children: props.children });
};
export default DynamicForm;
